import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

interface ProfileData {
  enrollmentStatus: string | null;
  engagementStatus: string | null;
  lastJobActivityAt: any;
  lastActiveAt: any;
  clientId: string | null;
}

interface ClientOption {
  id: string;
  name: string;
}

export function FirmDashboard() {
  const { firmAdmin } = useAuth();
  const { firmId, firm } = firmAdmin!;

  const [profiles, setProfiles] = useState<ProfileData[]>([]);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [clientFilter, setClientFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { load(); }, [firmId]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [profilesSnap, clientsSnap] = await Promise.all([
        getDocs(query(collection(db, 'profiles'), where('firmId', '==', firmId))),
        getDocs(query(collection(db, 'clients'), where('firmId', '==', firmId))),
      ]);

      setProfiles(
        profilesSnap.docs.map(d => {
          const p = d.data();
          return {
            enrollmentStatus: p.enrollmentStatus ?? null,
            engagementStatus: p.engagementStatus ?? null,
            lastJobActivityAt: p.lastJobActivityAt,
            lastActiveAt: p.lastActiveAt,
            clientId: p.clientId ?? null,
          };
        })
      );

      setClientOptions(
        clientsSnap.docs
          .map(d => ({ id: d.id, name: d.data().name ?? '' }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (e: any) {
      setError(e.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  };

  // Derive stats from filtered profiles — no re-fetch needed when filter changes.
  const scope = clientFilter
    ? profiles.filter(p => p.clientId === clientFilter)
    : profiles;

  const cutoffMs = Date.now() - 14 * 24 * 60 * 60 * 1000;
  let approved = 0, pending = 0, overdue = 0;
  for (const p of scope) {
    if (p.enrollmentStatus === 'approved') approved++;
    if (p.enrollmentStatus === 'pending') pending++;
    // Prefer lastJobActivityAt (job added or status changed) for an accurate
    // job-search signal. Fall back to lastActiveAt for candidates enrolled before
    // lastJobActivityAt was introduced so existing data doesn't spike the count.
    // Skip placed/paused candidates — both represent explained inactivity.
    const engStatus = p.engagementStatus ?? 'active';
    if (engStatus !== 'placed' && engStatus !== 'paused') {
      const activityMs = tsToMs(p.lastJobActivityAt) || tsToMs(p.lastActiveAt);
      if (activityMs < cutoffMs) overdue++;
    }
  }
  const stats = { totalEnrolled: scope.length, approved, pending, overdue };

  const termEndMs = tsToMs(firm.termEndDate);
  const termActive = firm.active && (termEndMs === 0 || termEndMs > Date.now());

  const selectedClientName = clientOptions.find(c => c.id === clientFilter)?.name ?? '';

  return (
    <Layout>
      <div style={{ maxWidth: 960 }}>
        <div className="page-header">
          <div>
            <h1 className="page-title">{firm.name}</h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <span className={`badge badge-${termActive ? 'active' : 'inactive'}`}>
                {termActive ? 'Active' : 'Term ended'}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {clientOptions.length > 0 && (
              <select
                className="form-input"
                value={clientFilter}
                onChange={e => setClientFilter(e.target.value)}
                style={{ maxWidth: 220, margin: 0, fontSize: 13 }}
              >
                <option value="">All Clients</option>
                {clientOptions.map(cl => (
                  <option key={cl.id} value={cl.id}>{cl.name}</option>
                ))}
              </select>
            )}
            <button
              className="btn-secondary"
              onClick={load}
              disabled={loading}
              style={{ padding: '6px 16px', fontSize: 13 }}
            >
              Refresh
            </button>
          </div>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 20 }}>{error}</div>}

        {loading && profiles.length === 0 ? (
          <div className="loading">Loading…</div>
        ) : (
          <>
            {/* Enrollment stats */}
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              {selectedClientName ? `Enrollment — ${selectedClientName}` : 'Enrollment'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
              <div className="stat-card">
                <div className="stat-label">Total Enrolled</div>
                <div className="stat-value">{stats.totalEnrolled}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Approved</div>
                <div className="stat-value" style={{ color: 'var(--accent-text)' }}>{stats.approved}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  Accepted coaching
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Pending Approval</div>
                <div className="stat-value" style={{ color: 'var(--warning)' }}>{stats.pending}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  Awaiting candidate
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">No Job Activity (14d+)</div>
                <div className="stat-value" style={{ color: stats.overdue > 0 ? 'var(--error)' : 'var(--text-primary)' }}>
                  {stats.overdue}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  No job added or status updated
                </div>
              </div>
            </div>

            {/* Client breakdown — always firm-wide, only when clients exist */}
            {!clientFilter && clientOptions.length > 0 && (() => {
              const knownIds = new Set(clientOptions.map(c => c.id));
              const breakdown = clientOptions.map(cl => {
                const cl_profiles = profiles.filter(p => p.clientId === cl.id);
                return {
                  id: cl.id,
                  name: cl.name,
                  total: cl_profiles.length,
                  approved: cl_profiles.filter(p => p.enrollmentStatus === 'approved').length,
                  pending: cl_profiles.filter(p => p.enrollmentStatus === 'pending').length,
                };
              }).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
              const unassigned_profiles = profiles.filter(p => !p.clientId || !knownIds.has(p.clientId));
              const unassigned = {
                total: unassigned_profiles.length,
                approved: unassigned_profiles.filter(p => p.enrollmentStatus === 'approved').length,
                pending: unassigned_profiles.filter(p => p.enrollmentStatus === 'pending').length,
              };
              return (
                <>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                    By Client
                  </div>
                  <div className="card" style={{ padding: 0, marginBottom: 24 }}>
                    <div className="table-container">
                      <table>
                        <thead>
                          <tr>
                            <th>Client</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th style={{ textAlign: 'right' }}>Approved</th>
                            <th style={{ textAlign: 'right' }}>Pending</th>
                          </tr>
                        </thead>
                        <tbody>
                          {breakdown.map(row => (
                            <tr key={row.id}>
                              <td style={{ fontSize: 13 }}>{row.name}</td>
                              <td style={{ textAlign: 'right', fontSize: 13, fontWeight: 500 }}>{row.total}</td>
                              <td style={{ textAlign: 'right', fontSize: 13, color: 'var(--accent-text)' }}>{row.approved}</td>
                              <td style={{ textAlign: 'right', fontSize: 13, color: 'var(--warning)' }}>{row.pending}</td>
                            </tr>
                          ))}
                          {unassigned.total > 0 && (
                            <tr>
                              <td style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>Unassigned</td>
                              <td style={{ textAlign: 'right', fontSize: 13, color: 'var(--text-muted)' }}>{unassigned.total}</td>
                              <td style={{ textAlign: 'right', fontSize: 13, color: 'var(--text-muted)' }}>{unassigned.approved}</td>
                              <td style={{ textAlign: 'right', fontSize: 13, color: 'var(--text-muted)' }}>{unassigned.pending}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Seat utilization — always firm-wide, not filtered */}
            {!clientFilter && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                  Seat Utilization
                </div>
                <div className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {firm.seatsUsed} of {firm.seatCount} seats consumed
                    </span>
                    <span style={{ fontSize: 22, fontWeight: 700 }}>
                      {firm.seatCount > 0 ? Math.round((firm.seatsUsed / firm.seatCount) * 100) : 0}%
                    </span>
                  </div>
                  <div style={{ background: 'var(--bg-secondary)', borderRadius: 6, height: 10, overflow: 'hidden' }}>
                    {(() => {
                      const pct = firm.seatCount > 0 ? (firm.seatsUsed / firm.seatCount) * 100 : 0;
                      return (
                        <div style={{
                          height: '100%',
                          width: `${Math.min(pct, 100)}%`,
                          background: pct >= 90 ? 'var(--error)' : 'var(--accent)',
                          borderRadius: 6,
                        }} />
                      );
                    })()}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                    {firm.seatCount - firm.seatsUsed} seats remaining · Seats are permanently consumed at enrollment
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
